import type { TemplateRegistration, TemplateData } from './types';
import { sanitizeText, sanitizeSeoText, escapeAttr } from './sanitize';
import { getIconSvg, renderStars, buildFontsUrl, buildNavLinks, NavDataFlags, truncateAtSentence } from './helpers';

export const glassMorph: TemplateRegistration = {
  meta: {
    id: 'glass-morph',
    name: 'Glass Morph',
    category: 'Modern',
    description: 'Frosted glass UI with depth layers, gradients, and iOS-style translucency',
    previewImage: 'https://images.unsplash.com/photo-1557682250-33bd709cbe85?w=800&q=80',
    supportedFeatures: ['contact-form', 'phone-display', 'address-map', 'business-hours', 'about-section', 'services-list', 'testimonials', 'google-reviews', 'social-links', 'faq', 'price-list', 'promotions', 'photo-gallery'],
    defaultEnabledFeatures: ['contact-form', 'phone-display', 'about-section', 'services-list', 'testimonials'],
  },
  render(data: TemplateData, enabledFeatures: Set<string>): string {
    const safeName = sanitizeText(data.businessName);
    const safeHeadline = sanitizeText(data.headline);
    const safeAbout = sanitizeText(data.aboutText);
    const safeSeoTitle = sanitizeSeoText(data.seo.title, 70);
    const safeSeoDesc = sanitizeSeoText(data.seo.description, 160);

    const phone = data.businessData?.phone || null;
    const contactEmail = data.businessData?.contactEmail || null;
    const rating = data.businessData?.rating || null;
    const reviewCount = data.businessData?.reviewCount || 0;
    const businessHours = data.businessData?.businessHours || [];
    const photos = data.businessData?.photos || [];
    const address = data.businessData?.address || '';
    const googleMapsUrl = data.businessData?.googleMapsUrl || null;
    const galleryImages = photos.slice(0, 6);

    const navDataFlags: NavDataFlags = {
      hasServices: data.services.length > 0,
      hasTestimonials: data.testimonials.length > 0,
      hasGalleryPhotos: galleryImages.length > 0,
      hasFaq: !!(data.faq && data.faq.length > 0),
      hasPriceList: !!(data.priceList && data.priceList.length > 0),
    };
    const navLinks = buildNavLinks(enabledFeatures, navDataFlags);
    const fontsUrl = buildFontsUrl(['Poppins', 'Inter']);

    const sections: string[] = [];

    // --- Google Reviews ---
    if (enabledFeatures.has('google-reviews') && rating) {
      sections.push(`
      <section class="gm-reviews">
        <div class="container">
          <div class="glass-card reviews-card">
            <div class="reviews-stars">${renderStars(Math.round(rating))}</div>
            <p class="reviews-text">${rating.toFixed(1)} &middot; ${reviewCount} Google Reviews</p>
          </div>
        </div>
      </section>`);
    }

    // --- Promotions ---
    if (enabledFeatures.has('promotions') && data.promotion) {
      const promoTitle = sanitizeText(data.promotion.title);
      const promoDesc = sanitizeText(data.promotion.description);
      const promoDiscount = sanitizeText(data.promotion.discount);
      sections.push(`
      <section class="gm-promo">
        <div class="container">
          <div class="glass-card promo-card">
            <div class="promo-glow"></div>
            <span class="promo-badge">${promoDiscount}</span>
            <h3>${promoTitle}</h3>
            <p>${promoDesc}</p>
          </div>
        </div>
      </section>`);
    }

    // --- About ---
    if (enabledFeatures.has('about-section')) {
      sections.push(`
      <section id="about" class="gm-section">
        <div class="container">
          <div class="section-header">
            <span class="section-tag">About</span>
            <h2>Who We Are</h2>
          </div>
          <div class="glass-card about-card">
            <div class="about-grid">
              <div class="about-text">
                <p>${safeAbout}</p>
              </div>
              <div class="about-stats">
                <div class="about-stat-box"><span class="stat-num">${data.services.length}+</span><span class="stat-label">Services</span></div>
                <div class="about-stat-box"><span class="stat-num">${data.testimonials.length}+</span><span class="stat-label">Reviews</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- Services ---
    if (enabledFeatures.has('services-list') && data.services.length > 0) {
      const serviceCards = data.services.map(s => {
        const sName = sanitizeText(s.name);
        const sDesc = sanitizeText(s.description);
        const icon = s.icon ? getIconSvg(s.icon) : getIconSvg('zap');
        return `
          <div class="glass-card service-card">
            <div class="service-icon">${icon}</div>
            <h3>${sName}</h3>
            <p>${sDesc}</p>
            ${s.price ? `<span class="service-price">${sanitizeText(s.price)}</span>` : ''}
          </div>`;
      }).join('');
      sections.push(`
      <section id="services" class="gm-section">
        <div class="container">
          <div class="section-header">
            <span class="section-tag">Services</span>
            <h2>What We Offer</h2>
          </div>
          <div class="services-grid">${serviceCards}</div>
        </div>
      </section>`);
    }

    // --- Photo Gallery ---
    if (enabledFeatures.has('photo-gallery') && galleryImages.length > 0) {
      const imgs = galleryImages.map(url => `<div class="gallery-item"><img src="${escapeAttr(url)}" alt="${escapeAttr(safeName)}" loading="lazy"></div>`).join('');
      sections.push(`
      <section id="gallery" class="gm-section">
        <div class="container">
          <div class="section-header">
            <span class="section-tag">Gallery</span>
            <h2>Our Work</h2>
          </div>
          <div class="gallery-grid">${imgs}</div>
        </div>
      </section>`);
    }

    // --- Testimonials ---
    if (enabledFeatures.has('testimonials') && data.testimonials.length > 0) {
      const testimonialCards = data.testimonials.map(t => {
        const tName = sanitizeText(t.name);
        const tContent = sanitizeText(t.content);
        const tRole = t.role ? sanitizeText(t.role) : '';
        return `
          <div class="glass-card testimonial-card">
            <div class="quote-mark">&ldquo;</div>
            <blockquote>${tContent}</blockquote>
            <div class="testimonial-footer">
              <div class="t-avatar">${tName.charAt(0)}</div>
              <div class="t-info">
                <strong>${tName}</strong>
                ${tRole ? `<span>${tRole}</span>` : ''}
              </div>
              ${t.rating ? `<div class="t-stars">${renderStars(t.rating)}</div>` : ''}
            </div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="testimonials" class="gm-section">
        <div class="container">
          <div class="section-header">
            <span class="section-tag">Reviews</span>
            <h2>Client Stories</h2>
          </div>
          <div class="testimonials-grid">${testimonialCards}</div>
        </div>
      </section>`);
    }

    // --- FAQ ---
    if (enabledFeatures.has('faq') && data.faq && data.faq.length > 0) {
      const items = data.faq.map(f => {
        const q = sanitizeText(f.question);
        const a = sanitizeText(f.answer);
        return `
          <div class="glass-card faq-item">
            <h3>${q}</h3>
            <p>${a}</p>
          </div>`;
      }).join('');
      sections.push(`
      <section id="faq" class="gm-section">
        <div class="container-narrow">
          <div class="section-header">
            <span class="section-tag">FAQ</span>
            <h2>Questions &amp; Answers</h2>
          </div>
          <div class="faq-list">${items}</div>
        </div>
      </section>`);
    }

    // --- Price List ---
    if (enabledFeatures.has('price-list') && data.priceList && data.priceList.length > 0) {
      const rows = data.priceList.map(p => {
        const pName = sanitizeText(p.name);
        const pDesc = sanitizeText(p.description);
        const pPrice = sanitizeText(p.price);
        return `
          <div class="glass-card price-row">
            <div class="price-info">
              <h3>${pName}</h3>
              <p>${pDesc}</p>
            </div>
            <span class="price-amount">${pPrice}</span>
          </div>`;
      }).join('');
      sections.push(`
      <section id="pricing" class="gm-section">
        <div class="container-narrow">
          <div class="section-header">
            <span class="section-tag">Pricing</span>
            <h2>Investment</h2>
          </div>
          <div class="price-list">${rows}</div>
        </div>
      </section>`);
    }

    // --- Business Hours ---
    if (enabledFeatures.has('business-hours') && businessHours.length > 0) {
      const hoursList = businessHours.map(h => `<li>${sanitizeText(h)}</li>`).join('');
      sections.push(`
      <section class="gm-section">
        <div class="container-narrow">
          <div class="section-header">
            <span class="section-tag">Hours</span>
            <h2>When We're Open</h2>
          </div>
          <div class="glass-card">
            <ul class="hours-list">${hoursList}</ul>
          </div>
        </div>
      </section>`);
    }

    // --- Contact ---
    if (enabledFeatures.has('contact-form')) {
      sections.push(`
      <section id="contact" class="gm-section">
        <div class="container-narrow">
          <div class="section-header">
            <span class="section-tag">Contact</span>
            <h2>Reach Out</h2>
          </div>
          <div class="glass-card contact-card">
            <div class="contact-details">
              ${enabledFeatures.has('phone-display') && phone ? `<a href="tel:${escapeAttr(phone)}" class="contact-chip">${getIconSvg('phone')}<span>${sanitizeText(phone)}</span></a>` : ''}
              ${enabledFeatures.has('address-map') && address ? `<div class="contact-chip">${getIconSvg('map-pin')}<span>${sanitizeText(address)}</span></div>` : ''}
              ${enabledFeatures.has('address-map') && googleMapsUrl ? `<a class="map-link" href="${escapeAttr(googleMapsUrl)}" target="_blank" rel="noopener noreferrer">View on Maps &rarr;</a>` : ''}
            </div>
            <form class="contact-form" ${contactEmail ? `action="https://formsubmit.co/${escapeAttr(contactEmail)}" method="POST"` : 'onsubmit="return false;"'}>
              <div class="form-row">
                <input type="text" placeholder="Name" required>
                <input type="email" placeholder="Email" required>
              </div>
              <textarea placeholder="Your message..." rows="5" required></textarea>
              <button type="submit">Send Message</button>
            </form>
          </div>
        </div>
      </section>`);
    }

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${safeSeoTitle}</title>
  <meta name="description" content="${safeSeoDesc}">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="${fontsUrl}" rel="stylesheet">
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    :root {
      --bg-start: #020617; --bg-end: #0f172a; --text: #f1f5f9; --text-muted: #94a3b8;
      --blue: #3b82f6; --purple: #8b5cf6; --glass-bg: rgba(255,255,255,0.06);
      --glass-border: rgba(255,255,255,0.1); --glass-shadow: rgba(0,0,0,0.2);
      --font-heading: 'Poppins', sans-serif; --font-body: 'Inter', sans-serif;
    }
    html { scroll-behavior: smooth; }
    body { font-family: var(--font-body); background: linear-gradient(160deg, var(--bg-start), var(--bg-end)); color: var(--text); line-height: 1.7; font-size: 16px; min-height: 100vh; position: relative; overflow-x: hidden; }
    body::before { content: ''; position: fixed; top: -200px; left: -200px; width: 500px; height: 500px; background: radial-gradient(circle, rgba(59,130,246,0.15), transparent 70%); border-radius: 50%; z-index: 0; pointer-events: none; }
    body::after { content: ''; position: fixed; bottom: -150px; right: -150px; width: 400px; height: 400px; background: radial-gradient(circle, rgba(139,92,246,0.12), transparent 70%); border-radius: 50%; z-index: 0; pointer-events: none; }
    .container { max-width: 1100px; margin: 0 auto; padding: 0 32px; position: relative; z-index: 1; }
    .container-narrow { max-width: 720px; margin: 0 auto; padding: 0 32px; position: relative; z-index: 1; }
    a { color: var(--blue); text-decoration: none; transition: color .2s; }
    a:hover { color: var(--purple); }

    /* GLASS UTILITY */
    .glass-card { background: var(--glass-bg); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px); border: 1px solid var(--glass-border); border-radius: 16px; box-shadow: 0 8px 32px var(--glass-shadow); }

    /* NAV */
    .navbar { padding: 16px 0; position: sticky; top: 0; z-index: 100; background: rgba(2,6,23,0.7); backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px); border-bottom: 1px solid var(--glass-border); }
    .nav-inner { display: flex; align-items: center; justify-content: space-between; max-width: 1100px; margin: 0 auto; padding: 0 32px; }
    .nav-brand { font-family: var(--font-heading); font-size: 1.2rem; font-weight: 600; color: var(--text); text-decoration: none; }
    .nav-brand:hover { color: var(--blue); }
    .nav-links { display: flex; gap: 24px; list-style: none; }
    .nav-links a { color: var(--text-muted); font-size: .88rem; font-weight: 500; text-decoration: none; transition: color .2s; }
    .nav-links a:hover { color: var(--text); }
    .nav-toggle { display: none; background: none; border: none; cursor: pointer; padding: 4px; }
    .nav-toggle span { display: block; width: 22px; height: 2px; background: var(--text); margin: 5px 0; border-radius: 2px; }

    /* HERO */
    .hero { min-height: 100vh; display: flex; align-items: center; justify-content: center; position: relative; overflow: hidden; background: repeating-linear-gradient(45deg, rgba(59,130,246,0.03) 0px, rgba(59,130,246,0.03) 2px, transparent 2px, transparent 12px), repeating-linear-gradient(-45deg, rgba(139,92,246,0.03) 0px, rgba(139,92,246,0.03) 2px, transparent 2px, transparent 12px), linear-gradient(160deg, var(--bg-start), var(--bg-end)); }
    .hero-card { position: relative; z-index: 1; max-width: 640px; margin: 0 32px; padding: 56px 48px; text-align: center; background: var(--glass-bg); backdrop-filter: blur(24px); -webkit-backdrop-filter: blur(24px); border: 1px solid var(--glass-border); border-radius: 20px; box-shadow: 0 24px 80px rgba(0,0,0,0.4); transform: rotate(-1deg); }
    .hero h1 { font-family: var(--font-heading); font-size: clamp(2.2rem, 5vw, 3.5rem); font-weight: 700; line-height: 1.2; margin-bottom: 20px; background: linear-gradient(135deg, var(--text), var(--blue)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
    .hero-sub { color: var(--text-muted); font-size: 1.05rem; line-height: 1.8; margin-bottom: 36px; max-width: 540px; margin-left: auto; margin-right: auto; }
    .hero-cta { display: inline-block; padding: 14px 40px; background: linear-gradient(135deg, var(--blue), var(--purple)); border: none; color: var(--text); font-family: var(--font-heading); font-weight: 600; font-size: .95rem; border-radius: 12px; transition: all .3s; text-decoration: none; }
    .hero-cta:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(59,130,246,0.3); color: var(--text); }

    /* SECTIONS */
    .gm-section { padding: 80px 0; }
    .section-header { text-align: center; margin-bottom: 48px; }
    .section-tag { display: inline-block; font-family: var(--font-heading); font-size: .75rem; font-weight: 600; letter-spacing: 2px; text-transform: uppercase; color: var(--blue); background: rgba(59,130,246,0.1); padding: 6px 14px; border-radius: 20px; margin-bottom: 12px; }
    .gm-section h2 { font-family: var(--font-heading); font-size: 1.8rem; font-weight: 700; color: var(--text); }

    /* ABOUT */
    .about-card { padding: 40px; }
    .about-grid { display: grid; grid-template-columns: 1fr auto; gap: 40px; align-items: center; }
    .about-text p { color: var(--text-muted); line-height: 1.9; }
    .about-stats { display: flex; flex-direction: column; gap: 16px; }
    .about-stat-box { background: rgba(59,130,246,0.1); border: 1px solid var(--glass-border); border-radius: 12px; padding: 20px 28px; text-align: center; min-width: 120px; }
    .about-stat-box .stat-num { font-family: var(--font-heading); font-size: 2rem; font-weight: 700; color: var(--blue); display: block; margin-bottom: 4px; }
    .about-stat-box .stat-label { font-size: .75rem; color: var(--text-muted); text-transform: uppercase; letter-spacing: 1px; }

    /* SERVICES */
    .services-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
    .service-card { padding: 32px 24px; text-align: center; transition: transform .2s, border-color .3s; }
    .service-card:hover { transform: translateY(-4px); border-color: rgba(59,130,246,0.3); }
    .service-icon { width: 48px; height: 48px; background: linear-gradient(135deg, rgba(59,130,246,0.15), rgba(139,92,246,0.15)); border-radius: 12px; display: flex; align-items: center; justify-content: center; margin: 0 auto 20px; color: var(--blue); }
    .service-icon .icon-char { font-size: 22px; line-height: 1; }
    .service-card h3 { font-family: var(--font-heading); font-size: 1.05rem; font-weight: 600; margin-bottom: 10px; }
    .service-card p { color: var(--text-muted); font-size: .9rem; line-height: 1.7; margin-bottom: 12px; }
    .service-price { color: var(--blue); font-family: var(--font-heading); font-weight: 600; font-size: .9rem; }

    /* GALLERY */
    .gallery-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
    .gallery-item img { width: 100%; height: 240px; object-fit: cover; border-radius: 12px; border: 1px solid var(--glass-border); transition: transform .3s; }
    .gallery-item img:hover { transform: scale(1.03); }

    /* TESTIMONIALS */
    .testimonials-grid { display: flex; flex-direction: column; gap: 24px; max-width: 700px; margin: 0 auto; }
    .testimonial-card { padding: 28px; position: relative; overflow: hidden; }
    .testimonial-card:nth-child(odd) { align-self: flex-start; margin-right: 10%; }
    .testimonial-card:nth-child(even) { align-self: flex-end; margin-left: 10%; }
    .quote-mark { font-family: var(--font-heading); font-size: 3rem; line-height: 1; color: rgba(59,130,246,0.2); margin-bottom: 8px; }
    .testimonial-card blockquote { font-size: .92rem; color: var(--text-muted); line-height: 1.8; margin-bottom: 20px; }
    .testimonial-footer { display: flex; align-items: center; gap: 12px; }
    .t-avatar { width: 38px; height: 38px; border-radius: 50%; background: linear-gradient(135deg, var(--blue), var(--purple)); color: var(--text); display: flex; align-items: center; justify-content: center; font-family: var(--font-heading); font-weight: 600; font-size: .85rem; }
    .t-info { flex: 1; }
    .t-info strong { font-size: .88rem; display: block; color: var(--text); }
    .t-info span { font-size: .78rem; color: var(--text-muted); }
    .t-stars { white-space: nowrap; }
    .star { color: rgba(255,255,255,0.2); font-size: .85rem; }
    .star.filled { color: #f59e0b; }

    /* GOOGLE REVIEWS */
    .gm-reviews { padding: 32px 0; }
    .reviews-card { padding: 20px 32px; display: flex; align-items: center; justify-content: center; gap: 16px; flex-wrap: wrap; }
    .reviews-text { color: var(--text-muted); font-size: .9rem; }
    .reviews-stars .star { font-size: 1.1rem; }

    /* PROMO */
    .gm-promo { padding: 40px 0; }
    .promo-card { padding: 36px; text-align: center; position: relative; overflow: hidden; }
    .promo-glow { position: absolute; top: -50px; left: 50%; transform: translateX(-50%); width: 200px; height: 100px; background: radial-gradient(ellipse, rgba(139,92,246,0.2), transparent 70%); pointer-events: none; }
    .promo-badge { display: inline-block; background: linear-gradient(135deg, var(--blue), var(--purple)); color: var(--text); padding: 6px 18px; border-radius: 20px; font-size: .82rem; font-weight: 600; margin-bottom: 14px; }
    .promo-card h3 { font-family: var(--font-heading); font-size: 1.15rem; font-weight: 600; margin-bottom: 8px; }
    .promo-card p { color: var(--text-muted); font-size: .92rem; }

    /* FAQ */
    .faq-list { display: flex; flex-direction: column; gap: 12px; }
    .faq-item { padding: 24px 28px; }
    .faq-item h3 { font-family: var(--font-heading); font-size: .98rem; font-weight: 600; margin-bottom: 10px; color: var(--text); }
    .faq-item p { color: var(--text-muted); font-size: .9rem; line-height: 1.7; }

    /* PRICE LIST */
    .price-list { display: flex; flex-direction: column; gap: 10px; }
    .price-row { display: flex; justify-content: space-between; align-items: center; padding: 20px 28px; }
    .price-info h3 { font-family: var(--font-heading); font-size: .98rem; font-weight: 600; margin-bottom: 4px; }
    .price-info p { color: var(--text-muted); font-size: .84rem; }
    .price-amount { font-family: var(--font-heading); font-size: 1.1rem; font-weight: 700; color: var(--blue); white-space: nowrap; }

    /* HOURS */
    .hours-list { list-style: none; padding: 20px 28px; }
    .hours-list li { padding: 12px 0; border-bottom: 1px solid var(--glass-border); color: var(--text-muted); font-size: .9rem; }
    .hours-list li:last-child { border-bottom: none; }

    /* CONTACT */
    .contact-card { padding: 36px; max-width: 600px; margin: 0 auto; }
    .contact-details { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 28px; justify-content: center; }
    .contact-chip { display: inline-flex; align-items: center; gap: 10px; background: rgba(255,255,255,0.04); border: 1px solid var(--glass-border); border-radius: 10px; padding: 10px 16px; font-size: .88rem; color: var(--text-muted); text-decoration: none; transition: all .2s; }
    .contact-chip:hover { background: rgba(59,130,246,0.1); border-color: var(--blue); color: var(--text); }
    .contact-chip .icon-char { font-size: 16px; line-height: 1; color: var(--blue); }
    .map-link { font-size: .85rem; color: var(--blue); font-weight: 500; }
    .contact-form { display: flex; flex-direction: column; gap: 14px; }
    .form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
    .contact-form input, .contact-form textarea { background: rgba(255,255,255,0.04); border: 1px solid var(--glass-border); border-radius: 10px; padding: 14px 16px; font-family: var(--font-body); font-size: .9rem; color: var(--text); outline: none; transition: border-color .2s; }
    .contact-form input:focus, .contact-form textarea:focus { border-color: var(--blue); background: rgba(59,130,246,0.05); }
    .contact-form input::placeholder, .contact-form textarea::placeholder { color: rgba(148,163,184,0.5); }
    .contact-form button { align-self: flex-start; padding: 14px 36px; background: linear-gradient(135deg, var(--blue), var(--purple)); border: none; color: var(--text); font-family: var(--font-heading); font-weight: 600; font-size: .92rem; border-radius: 10px; cursor: pointer; transition: transform .2s, box-shadow .3s; }
    .contact-form button:hover { transform: translateY(-2px); box-shadow: 0 4px 20px rgba(59,130,246,0.3); }

    /* FOOTER */
    .footer { padding: 40px 0; text-align: center; border-top: 1px solid var(--glass-border); position: relative; z-index: 1; }
    .footer p { font-size: .83rem; color: var(--text-muted); }

    /* RESPONSIVE */
    @media (max-width: 768px) {
      .nav-links { display: none; }
      .nav-links.show { display: flex; flex-direction: column; position: absolute; top: 100%; left: 0; right: 0; background: rgba(2,6,23,0.95); backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px); padding: 20px 32px; border-bottom: 1px solid var(--glass-border); gap: 12px; }
      .nav-toggle { display: block; }
      .hero { padding: 80px 0 60px; }
      .hero-card { transform: none; }
      .about-grid { grid-template-columns: 1fr; }
      .about-stats { flex-direction: row; justify-content: center; }
      .services-grid { grid-template-columns: 1fr 1fr; }
      .gallery-grid { grid-template-columns: repeat(2, 1fr); }
      .gm-section { padding: 60px 0; }
      .form-row { grid-template-columns: 1fr; }
    }
    @media (max-width: 480px) {
      .services-grid { grid-template-columns: 1fr; }
      .testimonial-card:nth-child(odd), .testimonial-card:nth-child(even) { margin-left: 0; margin-right: 0; }
      .gallery-grid { grid-template-columns: 1fr; }
      .price-row { flex-direction: column; align-items: flex-start; gap: 8px; }
    }
  </style>
</head>
<body>
  <nav class="navbar">
    <div class="nav-inner">
      <a href="#" class="nav-brand">${safeName}</a>
      <button class="nav-toggle" aria-label="Toggle menu" onclick="this.parentElement.querySelector('.nav-links').classList.toggle('show')">
        <span></span><span></span><span></span>
      </button>
      <ul class="nav-links">
        ${navLinks.map(l => `<li><a href="${l.href}">${l.label}</a></li>`).join('')}
      </ul>
    </div>
  </nav>

  <section class="hero">
    <div class="hero-card">
      <h1>${safeHeadline}</h1>
      <p class="hero-sub">${truncateAtSentence(safeAbout)}</p>
      ${enabledFeatures.has('contact-form') ? `<a href="#contact" class="hero-cta">Get Started</a>` : ''}
    </div>
  </section>

  ${sections.join('\n')}

  <footer class="footer">
    <div class="container">
      <p>${safeName}</p>
    </div>
  </footer>
</body>
</html>`;
  }
};
